/**
 * Phase 10f.2: Onboarding Checklist
 *
 * Two exports:
 *   OnboardingChecklistCard   — collapsible card rendered at top of DashboardPage
 *   OnboardingNavIndicator    — circular progress ring in the top nav that opens a Sheet
 *
 * Data: reuses useOnboardingStatus (refetchInterval: 30s handles auto-completions)
 * Visibility: shown when onboarding_status=in_progress, or completed with items remaining
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Check } from 'lucide-react';
import {
  useOnboardingStatus,
  useCompleteOnboarding,
} from '@/hooks/useOnboardingStatus';
import type { ChecklistItem } from '@/lib/schemas';

// ============================================================================
// ProgressRing — pure SVG, no library
// ============================================================================

interface ProgressRingProps {
  completed: number;
  total: number;
  size?: number;
}

function ProgressRing({ completed, total, size = 36 }: ProgressRingProps) {
  const r = (size / 2) - 4;
  const circumference = 2 * Math.PI * r;
  const fraction = total === 0 ? 0 : Math.min(completed / total, 1);
  const offset = circumference * (1 - fraction);
  const allDone = total > 0 && completed >= total;

  return (
    <div className="relative inline-flex items-center justify-center flex-shrink-0">
      <svg
        width={size}
        height={size}
        className="-rotate-90"
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth="3"
          className="stroke-muted"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
            transition: 'stroke-dashoffset 0.4s ease',
          }}
          className={allDone ? 'stroke-green-500' : 'stroke-amber-500'}
        />
      </svg>
      <span
        className="absolute text-[9px] font-semibold leading-none tabular-nums"
        style={{ fontSize: size < 32 ? '8px' : '9px' }}
      >
        {completed}/{total}
      </span>
    </div>
  );
}

// ============================================================================
// ConfettiPop — pure CSS keyframe animation, no library
// ============================================================================

function ConfettiPop({ active }: { active: boolean }) {
  if (!active) return null;
  const pieces = [
    { color: '#f59e0b', left: '15%', delay: '0s' },
    { color: '#10b981', left: '28%', delay: '0.08s' },
    { color: '#3b82f6', left: '42%', delay: '0.16s' },
    { color: '#8b5cf6', left: '58%', delay: '0.06s' },
    { color: '#ef4444', left: '72%', delay: '0.12s' },
    { color: '#f59e0b', left: '85%', delay: '0.04s' },
  ];

  return (
    <>
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; }
          100% { transform: translateY(80px) rotate(540deg) scale(0.4); opacity: 0; }
        }
        .confetti-piece {
          animation: confetti-fall 1s ease-out forwards;
          position: absolute;
          width: 8px;
          height: 8px;
          border-radius: 2px;
          top: 0;
          pointer-events: none;
        }
      `}</style>
      <div className="pointer-events-none absolute inset-x-0 top-0 overflow-hidden h-20" aria-hidden="true">
        {pieces.map((p, i) => (
          <div
            key={i}
            className="confetti-piece"
            style={{
              backgroundColor: p.color,
              left: p.left,
              animationDelay: p.delay,
            }}
          />
        ))}
      </div>
    </>
  );
}

// ============================================================================
// ChecklistRow — single item
// ============================================================================

interface ChecklistRowProps {
  item: ChecklistItem;
  onClick: () => void;
}

function ChecklistRow({ item, onClick }: ChecklistRowProps) {
  return (
    <button
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors',
        'hover:bg-muted/50 group',
        item.completed && 'opacity-60'
      )}
      onClick={onClick}
      data-testid={`checklist-item-${item.id}`}
    >
      {/* Checkbox */}
      <div
        className={cn(
          'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center',
          item.completed
            ? 'bg-green-500 border-green-500'
            : 'border-muted-foreground/40'
        )}
      >
        {item.completed && <Check className="w-3 h-3 text-white" />}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'text-sm leading-tight',
            !item.completed && 'font-medium',
            item.completed && 'line-through text-muted-foreground'
          )}
        >
          {item.title}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">
          {item.description}
        </div>
      </div>

      {/* Arrow — only on pending items */}
      {!item.completed && (
        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </button>
  );
}

// ============================================================================
// ChecklistItems — shared between card and sheet
// ============================================================================

interface ChecklistItemsProps {
  checklist: ChecklistItem[];
  onItemClick: (item: ChecklistItem) => void;
}

function ChecklistItems({ checklist, onItemClick }: ChecklistItemsProps) {
  return (
    <div className="space-y-0.5">
      {checklist.map((item) => (
        <ChecklistRow
          key={item.id}
          item={item}
          onClick={() => onItemClick(item)}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Visibility helper
// ============================================================================

function shouldShowChecklist(
  status: string | undefined,
  checklist: ChecklistItem[] | null | undefined
): boolean {
  if (!status || !checklist || checklist.length === 0) return false;
  if (status === 'skipped' || status === 'pending') return false;
  if (status === 'in_progress') return true;
  if (status === 'completed') {
    const completedCount = checklist.filter((i) => i.completed).length;
    return completedCount < checklist.length;
  }
  return false;
}

// ============================================================================
// OnboardingChecklistCard — dashboard card
// ============================================================================

export function OnboardingChecklistCard() {
  const { data, isLoading } = useOnboardingStatus();
  const completeOnboarding = useCompleteOnboarding();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [allCompleteShown, setAllCompleteShown] = useState(false);

  const checklist = data?.checklist?.items ?? null;
  const completedCount = checklist ? checklist.filter((i) => i.completed).length : 0;
  const totalCount = checklist ? checklist.length : 0;
  const allComplete = totalCount > 0 && completedCount >= totalCount;

  // Trigger confetti once when all items complete
  useEffect(() => {
    if (allComplete && !allCompleteShown) {
      setAllCompleteShown(true);
    }
  }, [allComplete, allCompleteShown]);

  if (isLoading || dismissed) return null;
  if (!shouldShowChecklist(data?.onboarding_status, checklist)) return null;

  const handleItemClick = (item: ChecklistItem) => {
    navigate(item.action_url);
  };

  const handleDismiss = () => {
    setDismissed(true);
    completeOnboarding.mutate();
  };

  return (
    <div className="relative" data-testid="onboarding-checklist">
      <ConfettiPop active={allCompleteShown} />
      <Card>
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors rounded-t-lg py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ProgressRing completed={completedCount} total={totalCount} />
                  <CardTitle className="text-base">Getting Started</CardTitle>
                </div>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform duration-200',
                    isOpen && 'rotate-180'
                  )}
                />
              </div>
            </CardHeader>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <CardContent className="pt-0 pb-4">
              <ChecklistItems
                checklist={checklist!}
                onItemClick={handleItemClick}
              />

              <div className="mt-4 pt-3 border-t text-center">
                <button
                  className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
                  onClick={handleDismiss}
                  data-testid="dismiss-checklist"
                >
                  Dismiss
                </button>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
}

// ============================================================================
// OnboardingNavIndicator — top nav progress ring + Sheet slide-over
// ============================================================================

export function OnboardingNavIndicator() {
  const { data } = useOnboardingStatus();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  const checklist = data?.checklist?.items ?? null;

  if (!shouldShowChecklist(data?.onboarding_status, checklist)) return null;

  const completedCount = checklist!.filter((i) => i.completed).length;
  const totalCount = checklist!.length;

  const handleItemClick = (item: ChecklistItem) => {
    setSheetOpen(false);
    navigate(item.action_url);
  };

  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={`Getting started: ${completedCount} of ${totalCount} complete`}
          data-testid="onboarding-nav-indicator"
        >
          <ProgressRing completed={completedCount} total={totalCount} size={32} />
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="w-80 sm:max-w-xs">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <ProgressRing completed={completedCount} total={totalCount} />
            Getting Started
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 overflow-y-auto">
          <ChecklistItems
            checklist={checklist!}
            onItemClick={handleItemClick}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
